import './index.css'
import { useState } from 'react'
import { fmt } from '../../lib/format'
import { useAnnualView } from '../../hooks/useAnnualView'
import type { AnnualViewProps, AnnualCategoryRow, AnnualMonth } from '../../types'

const CAT_COLORS = [
  '#E25C4A', '#2F8F5C', '#1F3D2E', '#B43F30', '#E8A33D',
  '#6B9BD1', '#557067', '#EE9385', '#1F6E43', '#B8AC93',
  '#D4896A', '#4A7C59',
]

const MONTH_LABELS = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez']

function fmtR(n: number)  { return `R$ ${fmt(n)}` }
function fmtR0(n: number) { return `R$ ${Math.round(n).toLocaleString('pt-BR')}` }

// ─── Year KPIs ─────────────────────────────────────────────────────────────────

function YearKPIs({ months, year }: { months: AnnualMonth[]; year: number }) {
  const income  = months.reduce((s, m) => s + m.income, 0)
  const expense = months.reduce((s, m) => s + m.expense, 0)
  const balance = income - expense
  const pct     = income > 0 ? Math.round((expense / income) * 100) : 0

  return (
    <div className="bc-summary">
      <div className="bc-card bc-summary__cell">
        <div className="bc-card__lbl">Receitas em {year}</div>
        <div className="bc-card__val bc-card__val--pos">{fmtR0(income)}</div>
        <div className="bc-card__sub">Soma de janeiro a dezembro</div>
      </div>
      <div className="bc-card bc-summary__cell">
        <div className="bc-card__lbl">Despesas em {year}</div>
        <div className="bc-card__val bc-card__val--neg">{fmtR0(expense)}</div>
        <div className="bc-card__sub">{pct}% da renda anual</div>
      </div>
      <div className="bc-card bc-summary__cell">
        <div className="bc-card__lbl">Saldo do ano</div>
        <div className={`bc-card__val ${balance < 0 ? 'bc-card__val--neg' : 'bc-card__val--pos'}`}>
          {balance < 0 ? '−' : '+'} {fmtR0(Math.abs(balance))}
        </div>
        <div className="bc-card__sub">
          {balance < 0 ? 'Despesas excederam receitas' : 'Você terminou o ano no positivo'}
        </div>
      </div>
    </div>
  )
}

// ─── Year Chart ─────────────────────────────────────────────────────────────────

function YearChart({ months, year }: { months: AnnualMonth[]; year: number }) {
  const [hovered, setHovered] = useState<number | null>(null)
  const max   = Math.max(...months.flatMap(m => [m.income, m.expense]), 1)
  const steps = [1, 0.75, 0.5, 0.25, 0] as const

  return (
    <div className="bc-card bc-yearchart">
      <div className="bc-list__header">
        <h3 className="bc-list__title">Receitas vs despesas · mês a mês</h3>
        <div className="bc-legend">
          <span><span className="bc-legend__sw" style={{ background: '#6FB68C' }} />Receitas</span>
          <span><span className="bc-legend__sw" style={{ background: '#EE9385' }} />Despesas</span>
        </div>
      </div>
      <div className="bc-yearchart__scroll">
      <div className="bc-yearchart__plot">
        <div className="bc-yearchart__yaxis">
          {steps.map(t => <span key={t}>{fmtR0(max * t)}</span>)}
        </div>
        <div className="bc-yearchart__bars">
          <div className="bc-yearchart__grid">
            {steps.map(t => <div key={t} />)}
          </div>
          {months.map((m, i) => {
            const ih  = (m.income  / max) * 100
            const eh  = (m.expense / max) * 100
            const bal = m.income - m.expense
            const isHov = hovered === i
            return (
              <div
                key={m.label}
                className={`bc-yearchart__col${isHov ? ' is-hover' : ''}`}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="bc-yearchart__pair">
                  <div className="bc-yearchart__bar bc-yearchart__bar--in"  style={{ height: `${ih}%` }} />
                  <div className="bc-yearchart__bar bc-yearchart__bar--out" style={{ height: `${eh}%` }} />
                </div>
                <div className="bc-yearchart__lbl">{m.label}</div>
                {isHov && (
                  <div className="bc-yearchart__tip">
                    <div className="bc-yearchart__tip-month">{m.label} {year}</div>
                    <div className="bc-yearchart__tip-row">
                      <span className="bc-yearchart__tip-dot is-in" />
                      Receitas
                      <span className="bc-yearchart__tip-val">{fmtR(m.income)}</span>
                    </div>
                    <div className="bc-yearchart__tip-row">
                      <span className="bc-yearchart__tip-dot is-out" />
                      Despesas
                      <span className="bc-yearchart__tip-val">{fmtR(m.expense)}</span>
                    </div>
                    <div className="bc-yearchart__tip-balance">
                      Saldo
                      <strong className={bal < 0 ? 'amt-neg' : 'amt-pos'}>
                        {bal < 0 ? '−' : '+'} {fmtR(Math.abs(bal))}
                      </strong>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      </div>
    </div>
  )
}

// ─── Months Table ───────────────────────────────────────────────────────────────

function MonthsTable({ months }: { months: AnnualMonth[] }) {
  const totIncome  = months.reduce((s, m) => s + m.income, 0)
  const totExpense = months.reduce((s, m) => s + m.expense, 0)
  const totBalance = totIncome - totExpense

  return (
    <div className="bc-card">
      <div className="bc-list__header">
        <h3 className="bc-list__title">Detalhe mensal</h3>
      </div>
      <div className="bc-yeartable__wrap">
        <table className="bc-yeartable">
          <thead>
            <tr>
              <th>Mês</th>
              <th className="num">Receitas</th>
              <th className="num">Despesas</th>
              <th className="num">% gasto</th>
              <th className="num">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {months.map(m => {
              const bal = m.income - m.expense
              const pct = m.income > 0 ? Math.round((m.expense / m.income) * 100) : 0
              return (
                <tr key={m.label}>
                  <td>{m.label}</td>
                  <td className="num">{fmt(m.income)}</td>
                  <td className="num">{fmt(m.expense)}</td>
                  <td className={`num ${pct >= 100 ? 'amt-neg' : pct >= 80 ? 'amt-warn' : 'amt-pos'}`}>
                    {pct}%
                  </td>
                  <td className={`num ${bal < 0 ? 'amt-neg' : 'amt-pos'}`}>
                    {bal < 0 ? '−' : '+'} {fmt(Math.abs(bal))}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td className="num">{fmt(totIncome)}</td>
              <td className="num">{fmt(totExpense)}</td>
              <td className="num">—</td>
              <td className={`num ${totBalance < 0 ? 'amt-neg' : 'amt-pos'}`}>
                {totBalance < 0 ? '−' : '+'} {fmt(Math.abs(totBalance))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

// ─── Year Categories ────────────────────────────────────────────────────────────

function YearCategories({ rows }: { rows: AnnualCategoryRow[] }) {
  const expenses = rows
    .filter(r => r.kind === 'out')
    .map(r => ({ id: r.id, name: r.name, amount: r.values.reduce((s, v) => s + v, 0) }))
    .filter(c => c.amount > 0)
    .sort((a, b) => b.amount - a.amount)
    .map((c, i) => ({ ...c, color: CAT_COLORS[i % CAT_COLORS.length] }))

  const total = expenses.reduce((s, c) => s + c.amount, 0)

  if (expenses.length === 0) {
    return (
      <div className="bc-card">
        <div className="bc-list__header">
          <h3 className="bc-list__title">Para onde foi o dinheiro</h3>
        </div>
        <p className="bc-empty">Nenhuma despesa categorizada este ano.</p>
      </div>
    )
  }

  return (
    <div className="bc-card">
      <div className="bc-list__header">
        <h3 className="bc-list__title">Para onde foi o dinheiro</h3>
        <span className="bc-yearcats__total">Total: {fmtR0(total)}</span>
      </div>
      <div className="bc-yearcats">
        <div className="bc-yearcats__bar">
          {expenses.map(c => (
            <div
              key={c.id}
              className="bc-yearcats__seg"
              style={{ flex: c.amount, background: c.color }}
              title={c.name}
            />
          ))}
        </div>
        <div className="bc-yearcats__list">
          {expenses.map(c => {
            const pct = total > 0 ? (c.amount / total) * 100 : 0
            return (
              <div key={c.id} className="bc-yearcats__row">
                <span className="bc-yearcats__dot" style={{ background: c.color }} />
                <span className="bc-yearcats__name">{c.name}</span>
                <span className="bc-yearcats__pct">{pct.toFixed(1)}%</span>
                <span className="bc-yearcats__amt">{fmtR0(c.amount)}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Annual Matrix ──────────────────────────────────────────────────────────────

type MatrixFilter = 'all' | 'in' | 'out'

function AnnualMatrix({ rows }: { rows: AnnualCategoryRow[] }) {
  const [filter, setFilter] = useState<MatrixFilter>('all')

  const incomeRows  = rows.filter(r => r.kind === 'in')
  const expenseRows = rows.filter(r => r.kind === 'out')

  const sumByMonth = (rs: AnnualCategoryRow[]) =>
    MONTH_LABELS.map((_, i) => rs.reduce((s, r) => s + r.values[i], 0))

  const incomeByMonth  = sumByMonth(incomeRows)
  const expenseByMonth = sumByMonth(expenseRows)
  const balanceByMonth = incomeByMonth.map((v, i) => v - expenseByMonth[i])
  const pctByMonth     = incomeByMonth.map((v, i) => v > 0 ? (expenseByMonth[i] / v) * 100 : 0)

  const sumRow       = (vals: number[]) => vals.reduce((s, v) => s + v, 0)
  const totalIncome  = sumRow(incomeByMonth)
  const totalExpense = sumRow(expenseByMonth)
  const totalBalance = totalIncome - totalExpense

  const FILTER_OPTS: { id: MatrixFilter; label: string }[] = [
    { id: 'all', label: 'Todos' },
    { id: 'in',  label: 'Entradas' },
    { id: 'out', label: 'Saídas' },
  ]

  const renderRow = (r: AnnualCategoryRow) => (
    <tr key={r.id}>
      <td className="cat">{r.name}</td>
      <td className="type">
        <span className={`type-pill ${r.kind}`}>{r.kind === 'in' ? 'Entrada' : 'Saída'}</span>
      </td>
      {r.values.map((v, j) => (
        <td key={j} className={`num${v === 0 ? ' is-empty' : ''}`}>
          {v === 0 ? '—' : fmt(v)}
        </td>
      ))}
      <td className="num total">{fmt(sumRow(r.values))}</td>
    </tr>
  )

  const colspan = 2 + MONTH_LABELS.length + 1

  return (
    <div className="bc-card">
      <div className="bc-list__header">
        <h3 className="bc-list__title">Lançamentos do ano · matriz por categoria</h3>
        <div className="bc-matrix__filter">
          {FILTER_OPTS.map(o => (
            <button
              key={o.id}
              onClick={() => setFilter(o.id)}
              className={`bc-matrix__filter-btn${filter === o.id ? ' is-active' : ''}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="bc-matrix__wrap">
        <table className="bc-matrix">
          <thead>
            <tr>
              <th className="cat">Categoria</th>
              <th className="type">Tipo</th>
              {MONTH_LABELS.map(m => <th key={m}>{m}</th>)}
              <th className="total">Total</th>
            </tr>
          </thead>
          <tbody>
            {filter !== 'out' && incomeRows.length > 0 && (
              <tr className="is-section">
                <td className="cat" colSpan={colspan}>Entradas</td>
              </tr>
            )}
            {filter !== 'out' && incomeRows.map(renderRow)}
            {filter !== 'in' && expenseRows.length > 0 && (
              <tr className="is-section">
                <td className="cat" colSpan={colspan}>Saídas</td>
              </tr>
            )}
            {filter !== 'in' && expenseRows.map(renderRow)}
          </tbody>
          <tfoot>
            {filter !== 'out' && (
              <tr>
                <td className="cat">Total de receitas</td>
                <td />
                {incomeByMonth.map((v, i) => <td key={i} className="num">{fmt(v)}</td>)}
                <td className="num">{fmt(totalIncome)}</td>
              </tr>
            )}
            {filter !== 'in' && (
              <tr>
                <td className="cat">Total de despesas</td>
                <td />
                {expenseByMonth.map((v, i) => <td key={i} className="num">{fmt(v)}</td>)}
                <td className="num">{fmt(totalExpense)}</td>
              </tr>
            )}
            {filter === 'all' && (
              <>
                <tr className="pct">
                  <td className="cat">% de gastos</td>
                  <td />
                  {pctByMonth.map((p, i) => (
                    <td key={i} className={`num${p >= 100 ? ' over' : p >= 80 ? ' warn' : ''}`}>
                      {p.toFixed(0)}%
                    </td>
                  ))}
                  <td className="num">
                    {totalIncome > 0 ? ((totalExpense / totalIncome) * 100).toFixed(0) : 0}%
                  </td>
                </tr>
                <tr className="balance">
                  <td className="cat">Saldo</td>
                  <td />
                  {balanceByMonth.map((b, i) => (
                    <td key={i} className={`num${b < 0 ? ' is-neg' : ' is-pos'}`}>
                      {b < 0 ? '−' : '+'} {fmt(Math.abs(b))}
                    </td>
                  ))}
                  <td className={`num${totalBalance < 0 ? ' is-neg' : ' is-pos'}`}>
                    {totalBalance < 0 ? '−' : '+'} {fmt(Math.abs(totalBalance))}
                  </td>
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  )
}

// ─── Main export ──────────────────────────────────────────────────────────────────

export default function AnnualView({ householdId, year }: AnnualViewProps) {
  const { months, categoryRows, loading } = useAnnualView(householdId, year)

  if (loading && months.length === 0) {
    return (
      <section className="bc-section">
        <div className="bc-loading" style={{ minHeight: 'auto', padding: '48px 0' }}>
          Carregando…
        </div>
      </section>
    )
  }

  return (
    <>
      <section className="bc-section">
        <YearKPIs months={months} year={year} />
      </section>
      <section className="bc-section">
        <YearChart months={months} year={year} />
      </section>
      <section className="bc-section bc-grid-2">
        <MonthsTable months={months} />
        <YearCategories rows={categoryRows} />
      </section>
      <section className="bc-section">
        <AnnualMatrix rows={categoryRows} />
      </section>
    </>
  )
}
